import {Button, Checkbox, Form, Input, InputNumber, Popconfirm} from 'antd';
import {useForm} from 'antd/es/form/Form';
import cn from 'classnames';
import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {v4 as uid} from 'uuid';

import {IconsMap} from 'components/dynamic-icon';
import {CustomSelect} from 'components/form/inputs/custom-select';
import {DateCell} from 'components/table/columns/date-cell/date-cell';
import {
    IPredicateExpressionGroupRef,
    IPredicateExpressionOperandRef,
    TPredicateExpressionNode,
    EFilterValueType,
    getFilterValueTypeByColumnType
} from 'features/predicate-expression-form-list';
import {DATE, DATE_DMY} from 'shared/constants/date-format';

import {TRulesType, TRuleProp} from '../../api/types';
import {RulesGroup} from './rules-group';
import styles from './rules.module.less';

interface IRuleOperandProps {
    operand?: TRulesType;
    remove: () => void;
    depth: number;
    itemIndex: string;
    // Для проверки isDirty и isEmpty
    onRulesChange?: (operands?: TRuleProp[]) => void;
}

export const RulesOperand = forwardRef<IPredicateExpressionOperandRef, IRuleOperandProps>(
    ({operand, remove, depth, itemIndex, onRulesChange}: IRuleOperandProps, ref) => {
        const [form] = useForm();

        const expressionRef = useRef<IPredicateExpressionGroupRef>(null);

        const [tableName, setTableName] = useState<string>('');

        const [typeGroup, setTypeGroup] = useState<string>('');

        const [isComparisonValueAllowedEmpty, setIsComparisonValueAllowedEmpty] = useState(false);

        const [comparisonValueFieldType, setComparisonValueFieldType] = useState<string>('');

        const getValues = async (skipValidation = false) => {
            if (!Array.isArray(operand)) {
                const isValid = await (async () => {
                    let result = true;
                    try {
                        await form.validateFields();
                    } catch (e) {
                        if (e?.errorFields?.length) result = false;
                    }
                    return result;
                })();

                if (!isValid && !skipValidation) return Promise.reject(new Error('Ошибка валидации формы'));

                const values: TRulesType = {
                    fieldName: form.getFieldValue('fieldName'),
                    comparisonValue: form.getFieldValue('comparisonValue'),
                    enabled: form.getFieldValue('enabled'),
                    operatorCode: form.getFieldValue('operatorCode'),
                    tableName: form.getFieldValue('tableName'),
                };

                return values;
            }
            return expressionRef.current?.getValues?.(skipValidation);
        };

        useImperativeHandle(ref, () => ({getValues}));

        useEffect(() => {
            form.resetFields();
            if (!Array.isArray(operand)) {
                setIsComparisonValueAllowedEmpty(
                    operand?.operatorCode === 'EMPTY' || operand?.operatorCode === 'NOT_EMPTY'
                );
            }
        }, [operand]);

        const groupIdentificator = useMemo(() => {
            if (!Array.isArray(operand)) return undefined;
            let ident = operand[0];
            if (!ident) return {connectBy: 'OR' as 'OR' | 'AND', enabled: false, uid: uid()};
            if (!ident.uid) ident = {...ident, uid: uid()};
            if (!ident.enabled) ident = {...ident, enabled: false};
            return ident;
        }, [operand]);

        if (!Array.isArray(operand)) {
            return (
                <Form
                    layout="vertical"
                    form={form}
                    onValuesChange={(changed) => {
                        onRulesChange?.([operand]);
                        if (changed.tableName) {
                            form.setFieldsValue({
                                fieldName: undefined,
                                operatorCode: undefined,
                                comparisonValue: undefined,
                            });
                        }
                        if (changed.fieldName) {
                            form.setFieldsValue({operatorCode: undefined, comparisonValue: undefined});
                        }
                        if (changed.operatorCode) {
                            form.setFieldsValue({comparisonValue: undefined});
                        }
                    }}
                >
                    <div className={styles['rules-group-operand__item']}>
                        <div className={styles['rules-group-operand__item__body']}>
                            <div className={cn(styles['rules-group-operand__item__body__title'], 'fw-500')}>
                                Правило №{itemIndex}
                            </div>
                            <div className={styles['rules-group-operand__item__body__form']}>
                                <Form.Item
                                    className={styles['rules-group-operand__item__body__form-item__select']}
                                    name="tableName"
                                    label="Таблица"
                                    rules={[{required: true, message: 'Пожалуйста, выберите таблицу'}]}
                                    initialValue={operand?.tableName}
                                    required
                                >
                                    <CustomSelect
                                        style={{minWidth: 250}}
                                        settings={{
                                            url: '/ais/valueLists/RISK_DOC_SOURCE',
                                            valuePath: 'lookupCode',
                                            labelPath: 'description',
                                            placeholder: 'Выберите таблицу',
                                        }}
                                        handleTriggerEvent={(value) => {
                                            setTableName(value.lookupCode);
                                        }}
                                        fixedDropdown={false}
                                    />
                                </Form.Item>
                                <Form.Item
                                    className={styles['rules-group-operand__item__body__form-item__select']}
                                    label="Поле таблицы"
                                    required
                                    name="fieldName"
                                    rules={[{required: true, message: 'Пожалуйста, выберите поле'}]}
                                    initialValue={operand?.fieldName}
                                >
                                    <CustomSelect
                                        style={{minWidth: 250}}
                                        settings={{
                                            url: `/ais3.spv.risk.codes/svc-risk-codes-docs-mapping/columns?tableName=${tableName}`,
                                            valuePath: 'name',
                                            labelPath: 'comment',
                                            placeholder: 'Выберите поле',
                                        }}
                                        handleTriggerEvent={(value) => {
                                            setTypeGroup(value.typeGroup);
                                            setComparisonValueFieldType(getFilterValueTypeByColumnType(value?.type));
                                        }}
                                        fixedDropdown={false}
                                    />
                                </Form.Item>

                                <Form.Item
                                    className={styles['rules-group-operand__item__body__form-item__select']}
                                    label="Условие"
                                    required
                                    name="operatorCode"
                                    rules={[{required: true, message: 'Пожалуйста, выберите условие'}]}
                                    initialValue={operand?.operatorCode}
                                >
                                    <CustomSelect
                                        style={{minWidth: 250}}
                                        settings={{
                                            url: `/valueLists/TABLE_REPORT_FILTER_VIEW?sublistCode=${typeGroup}`,
                                            labelPath: 'meaning',
                                            valuePath: 'lookupCode',
                                            placeholder: 'Выберите условие',
                                        }}
                                        handleTriggerEvent={(value) => {
                                            setIsComparisonValueAllowedEmpty(
                                                value?.lookupCode === 'EMPTY' || value?.lookupCode === 'NOT_EMPTY'
                                            );
                                        }}
                                        fixedDropdown={false}
                                    />
                                </Form.Item>

                                <Form.Item
                                    className={styles['rules-group-operand__item__body__form-item__input']}
                                    label="Значение"
                                    required={!isComparisonValueAllowedEmpty}
                                    name="comparisonValue"
                                    rules={[
                                        {
                                            required: !isComparisonValueAllowedEmpty,
                                            message: 'Пожалуйста, введите значение',
                                        },
                                    ]}
                                    initialValue={operand?.comparisonValue}
                                >
                                    {(() => {
                                        if (comparisonValueFieldType === EFilterValueType.number) {
                                            return (
                                                <InputNumber
                                                    disabled={isComparisonValueAllowedEmpty}
                                                    style={{minWidth: 250}}
                                                    className="w-100"
                                                    placeholder="Введите значение"
                                                    decimalSeparator=","
                                                />
                                            );
                                        }
                                        if (comparisonValueFieldType === EFilterValueType.date) {
                                            return (
                                                <DateCell
                                                    disabled={isComparisonValueAllowedEmpty}
                                                    style={{minWidth: 250}}
                                                    format={DATE_DMY}
                                                    requestFormat={DATE}
                                                />
                                            );
                                        }
                                        return (
                                            <Input
                                                disabled={isComparisonValueAllowedEmpty}
                                                style={{minWidth: 250}}
                                                placeholder="Введите значение"
                                            />
                                        );
                                    })()}
                                </Form.Item>

                                <Form.Item
                                    className={cn(
                                        'ml-1',
                                        styles['rules-group-operand__item__body__form-item__checkbox']
                                    )}
                                    label={<>&nbsp;</>}
                                    name="enabled"
                                    initialValue={operand?.enabled}
                                    valuePropName="checked"
                                >
                                    <Checkbox>Активно</Checkbox>
                                </Form.Item>

                                <Popconfirm
                                    title={<span>Вы действительно хотите удалить правило?</span>}
                                    placement="bottomLeft"
                                    okText="Удалить"
                                    onConfirm={() => {
                                        remove?.();
                                    }}
                                >
                                    <div>
                                        <Button
                                            className={cn(
                                                styles['rules-group-operand__item__body__form-button'],
                                                'btn-only-icon',
                                                'button-critic'
                                            )}
                                            type="default"
                                        >
                                            <IconsMap.TrashXOutlined />
                                        </Button>
                                    </div>
                                </Popconfirm>
                            </div>
                        </div>
                    </div>
                </Form>
            );
        }

        return (
            <div className={styles['rules-group-operand__group']}>
                <RulesGroup
                    initialExpression={[
                        groupIdentificator ?? {connectBy: 'OR', uid: uid()},
                        ...(operand.slice(1) as TPredicateExpressionNode[]),
                    ]}
                    ref={expressionRef}
                    isChild
                    remove={() => {
                        remove?.();
                    }}
                    depth={depth + 1}
                    itemIndex={itemIndex}
                    onRulesChange={onRulesChange}
                />
            </div>
        );
    }
);
